"use client";
import { Meeting, columns } from "./table/columns";
import { DataTable } from "./table/data-table";
import {
	DropdownMenuSeparator,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	PayPalScriptProvider,
	PayPalButtons,
	usePayPalScriptReducer,
} from "@paypal/react-paypal-js";

import { sortBy } from "lodash";
import { useRouter } from "next/navigation";

import { env } from "@/env.mjs";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../ui/button";
import { MoreHorizontal } from "lucide-react";
import { MeetingStatus } from "@/models/meeting-status";
import { UserStatus } from "@/models/user-status";
import { debugLog } from "@/services/logging";

export default function Transactions({ userId }: { userId: string }) {
	const [selectedMeeting, setSelectedMeeting] = useState() as any;
	const [loading, setLoading] = useState(true);

	const meetingColumns: ColumnDef<Meeting>[] = [
		{
			accessorKey: "name",
			header: () => <div>Name</div>,
		},
		{
			accessorKey: "date",
			header: () => <div>Date</div>,
		},
		{
			accessorKey: "attendee",
			header: () => <div>Attendee</div>,
			cell: ({ row }) => (
				<div className="font-medium">{row.getValue("attendee")}</div>
			),
		},
		{
			accessorKey: "status",
			header: () => <div>Status</div>,
		},
		{
			id: "actions",
			cell: ({ row }) => {
				setSelectedMeeting(row.original);
				return (
					<Dialog>
						<DialogTrigger asChild>
							<Button variant="ghost" className="h-8 w-8 p-0">
								<span className="sr-only">Open menu</span>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Meeting Details</DialogTitle>
							</DialogHeader>
							<div className="">
								<p>
									<b>Meeting ID:</b> {row.original.meetingId}
								</p>
								<p>
									<b>Meeting Name:</b> {row.original.name}
								</p>
								<p>
									<b>Attendee:</b> {row.original.attendee}
								</p>
								<p>
									<b>Status:</b> {row.original.status}
								</p>
								<p>
									<b>Outstanding Fees:</b> $
									{row.original.status === MeetingStatus.PAYMENT
										? 1.99
										: 0}
								</p>
							</div>
							{row.original.status === MeetingStatus.PAYMENT && (
								<PayPalScriptProvider
									options={{
										clientId: env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
										components: "buttons",
										vault: true,
									}}>
									{" "}
									<MeetingPay />
								</PayPalScriptProvider>
							)}
						</DialogContent>
					</Dialog>
				);
			},
		},
	];

	const gridRef = useRef() as any; // Optional - for accessing Grid's API
	const [rowData, setRowData] = useState<Meeting[]>([]); // Set rowData to Array of Objects, one Object per Row
	const router = useRouter();

	const MeetingPay = () => {
		const [{ options, isPending }, dispatch] = usePayPalScriptReducer();

		const createOrder = (data: any, actions: any) => {
			return actions.order.create({
				purchase_units: [
					{
						amount: {
							value: "1.99",
						},
					},
				],
			});
		};

		const onApprove = (data: any, actions: any) => {
			return actions.order.capture().then(async function (details: any) {
				await fetch("/api/meeting/end", {
					method: "POST",
					body: JSON.stringify({
						orderId: data.orderID,
						userId,
						userStatus: UserStatus.ACTIVE,
						meetingStatus: MeetingStatus.ENDED,
						meetingId: selectedMeeting.meetingId,
					}),
				});
				router.push(`/profile/${userId}`);
			});
		};

		return (
			<div className="col text-center justify-content-center align-self-center">
				{isPending && <div className="spinner" />}
				<PayPalButtons
					style={{
						shape: "rect",
						color: "gold",
						layout: "vertical",
						label: "paypal",
					}}
					createOrder={(data: any, actions: any) =>
						createOrder(data, actions)
					}
					onApprove={(data: any, actions: any) => onApprove(data, actions)}
				/>
			</div>
		);
	};

	const getAllMeetings = async (data: any) => {
		const meetings = data.map(async (d: any) => {
			const res = await fetch(`/api/meeting?meetingId=${d.meetingId}`);
			const meet = await res.json();
			return {
				owner: d.owner,
				status: d.status,
				meetingId: meet?.meetingId,
				name: meet?.name,
				attendees: meet?.attendees,
				date: meet?.date,
			};
		});
		return Promise.all(meetings);
	};

	const processResponse = async (data: any) => {
		const allMeetings = await getAllMeetings(data);
		debugLog("All Meeting", allMeetings);
		const sortedData = sortBy(allMeetings, function (o) {
			return new Date(o?.date);
		}).reverse();

		const meetingPromises = sortedData.map(async (d: any) => {
			let attendeeId = d.attendees?.find((a: any) => a !== userId);

			let attendeeName = "";
			if (attendeeId) {
				const res = await fetch(`/api/user/summary?id=${attendeeId}`);
				let attendee = await res.json();
				if (attendee?.firstName && attendee?.lastName) {
					attendeeName = attendee?.firstName + " " + attendee?.lastName;
				}
			}
			return {
				meetingId: d.meetingId,
				name: d?.name,
				date: d?.date?.substring(0, 10),
				attendee: attendeeName,
				status: d?.status,
			} as Meeting;
		});

		const meetings = await Promise.all(meetingPromises);
		debugLog("Row Data", meetings);
		setRowData(meetings);
	};
	// Example load data from sever
	useEffect(() => {
		fetch(`/api/user/meeting?userId=${userId}`)
			.then((res: any) => res.json())
			.then((data: any) => {
				processResponse(data);
				setLoading(false);
			});
	}, [userId]);

	return (
		<div className="flex flex-col items-center space-y-7">
			<h1 className="font-semibold text-xl">Meetings</h1>
			{loading ? (
				<div
					role="status"
					className="flex flex-col justify-center items-center">
					<svg
						aria-hidden="true"
						className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-500"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>

					<p className="font-semibold text-gray-400">Loading</p>
				</div>
			) : (
				<DataTable columns={meetingColumns} data={rowData} />
			)}
		</div>
	);
}
